<template>
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <h2 class="text-lg font-semibold mb-4 mt-20 text-center">Record Donation</h2>
    <form>
      <table class="min-w-full bg-white border border-gray-200">
        <thead>
          <tr class="bg-gray-100 text-left">
            <th class="px-4 py-2 border-b font-semibold border-gray-200">
              PLEASE CONFIRM AND SIGN BEFORE DONATING
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in declarationItems" :key="index" class="hover:bg-gray-50">
            <td class="px-4 py-2 border-b border-gray-200">
              <div class="flex items-center gap-x-3">
                <label class="flex items-center cursor-pointer">
                  <input type="checkbox" class="hidden" :id="item.id" v-model="localFormData.declaration[item.model]" />
                  <span
                    class="form-checkbox h-4 w-4 inline-block border-2 border-custom-yellow transition duration-200 ease-in-out flex items-center justify-center">
                    <svg class="hidden w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </span>
                  <span class="ml-4 block text-sm font-semibold leading-6 text-gray-900">{{
                    item.label
                  }}</span>
                </label>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="bg-gray-50 py-8 px-2">
        <div class="max-w-4xl mx-auto">
          <div class="bg-white rounded-lg shadow-lg p-2">
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Please sign in the box below:
              </label>
              <div class="border-2 border-dashed border-gray-300 rounded-lg p-1 bg-gray-50">
                <canvas ref="signatureCanvas"
                  class="border-2 border-gray-400 rounded cursor-crosshair bg-white mx-auto block"
                  @mousedown="startDrawing" @mousemove="draw" @mouseup="stopDrawing" @mouseleave="stopDrawing"
                  @touchstart="startDrawing" @touchmove="draw" @touchend="stopDrawing"
                  style="touch-action: none"></canvas>
              </div>
              <p class="text-sm text-gray-600 mt-2 text-center">
                Use your mouse or touch to sign above
              </p>
            </div>
            <div
              class="flex flex-wrap gap-4 justify-end sm:justify-end sm:flex-row flex-col items-stretch sm:items-center">
              <button type="button" @click="clearSignature"
                class="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium w-full sm:w-auto">
                Clear Signature
              </button>
              <button type="button" @click="saveSignature"
                class="px-6 py-2 bg-orange-400 text-white rounded-lg hover:bg-orange-500 transition-colors duration-200 font-medium w-full sm:w-auto">
                Save Signature
              </button>
            </div>
            <div v-if="signatureData" class="mt-8 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h3 class="text-lg font-semibold text-orange-600 mb-2">
                Signature Preview:
              </h3>
              <img :src="signatureData" alt="Signature Preview"
                class="border border-gray-300 rounded bg-white max-w-full h-auto" />
              <p class="text-sm text-orange-600 mt-2">
                ✓ Signature captured successfully
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-between">
        <button type="button" class="px-4 py-2 border rounded text-orange-500 border-orange-500"
          @click="moveToPrevious">
          Back
        </button>
        <CustomButton @click="handleFinalSubmit" type="submit" label="Submit" class="px-4 py-2"
          bg-class="bg-custom-yellow" />
      </div>
    </form>
  </div>
</template>

<script>
import CustomButton from "@/components/CustomButton.vue";
import AWS from 'aws-sdk';

export default {
  name: "NewDonorDeclarationForm",
  components: {
    CustomButton,
  },
  data() {
    return {
      localFormData: { ...this.formData },
      isDrawing: false,
      signatureData: "",
      signature_url: "",
      isEmpty: true,
      lastX: 0,
      lastY: 0,
      ctx: null,
      declarationItems: [
        {
          id: "readPamphlet",
          model: "read_educational_material",
          label: 'I have read and understood the pamphlet on "BLOOD DONATION".',
        },
        {
          id: "correctAnswer",
          model: "consent_checkbox",
          label:
            "I have accurately answered each of the above questions to the best of my knowledge and volunteer to donate blood.",
        },
        {
          id: "bloodConsent",
          model: "blood_testing_consent",
          label:
            "I understand that my blood will be used for treatment of patients and consent to my blood being tested for HIV, Hepatitis B, Hepatitis C, VDRL(Syphilis), and Malaria.",
        },
        {
          id: "testResults",
          model: "terms_agreed",
          label:
            "I understand that I will be informed of my test results if I choose to.",
        },
        {
          id: "consent",
          model: "consent_form",
          label:
            "I consent to further communication by Hospital Donor Center regarding future donations.",
        },
      ],
    };
  },

  mounted() {
    this.setupCanvas();
  },

  watch: {
    localFormData: {
      handler(newValue) {
        this.$emit("update:formData", newValue);
      },
      deep: true,
    },
  },

  props: {
    formData: Object,
  },

  methods: {
    handleFinalSubmit() {
      if (!this.signature_url) {
        alert("Please save your signature before submitting.");
        return;
      }
      this.$emit("final-submit", {
        ...this.localFormData,
        declaration: {
          ...this.localFormData.declaration,
          signature_url: this.signature_url,
        },
      });
    },

    moveToPrevious() {
      this.$emit("move-to-previous-step");
    },

    setupCanvas() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext("2d");

      const rect = canvas.getBoundingClientRect();
      const scale = window.devicePixelRatio || 1;
      canvas.width = rect.width * scale;
      canvas.height = 200 * scale;

      ctx.scale(scale, scale);

      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      this.ctx = ctx;
    },

    startDrawing(e) {
      this.isDrawing = true;
      this.isEmpty = false;

      const rect = this.$refs.signatureCanvas.getBoundingClientRect();
      this.lastX = (e.clientX || e.touches[0].clientX) - rect.left;
      this.lastY = (e.clientY || e.touches[0].clientY) - rect.top;
    },

    draw(e) {
      if (!this.isDrawing) return;

      e.preventDefault();

      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();

      const currentX = (e.clientX || e.touches[0].clientX) - rect.left;
      const currentY = (e.clientY || e.touches[0].clientY) - rect.top;

      this.ctx.beginPath();
      this.ctx.moveTo(this.lastX, this.lastY);
      this.ctx.lineTo(currentX, currentY);
      this.ctx.stroke();

      this.lastX = currentX;
      this.lastY = currentY;
    },

    stopDrawing() {
      this.isDrawing = false;
    },

    clearSignature() {
      const canvas = this.$refs.signatureCanvas;

      this.ctx.fillStyle = "#fff";
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);

      this.isEmpty = true;
      this.signatureData = "";
      this.signature_url = "";
    },

    async saveSignature() {
      // if (this.isEmpty) {
      //   alert("Please provide a signature before saving.");
      //   return;
      // }

      const canvas = this.$refs.signatureCanvas;
      const dataURL = canvas.toDataURL("image/png");
      this.signatureData = dataURL;

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          alert("Failed to create signature file. Please try again.");
          return;
        }

        // Generate a unique filename for the signature
        const timestamp = Date.now();
        const filename = `signature_${timestamp}.png`;

        // Configure AWS SDK
        const s3 = new AWS.S3({
          endpoint: new AWS.Endpoint('https://blr1.digitaloceanspaces.com'),
          signatureVersion: 'v4',
          accessKeyId: 'DO00PK2KLQ4HUBKTB6R3',
          secretAccessKey: 'L3yOKmH3Dqfpc3tVZv4Mz5O+jd1bXTobKS//zmY72Rk',
          region: 'blr1',
        });

        const params = {
          Bucket: 'jiji-bucket',
          Key: filename,           // Use generated filename
          Body: blob,             // Use the blob from canvas
          ACL: 'public-read',
          ContentType: 'image/png', // Specify correct content type
        };

        try {
          const result = await s3.upload(params).promise();
          console.log(result);
          this.signature_url = result.Location;
          // alert("Signature saved successfully! URL: " + this.signature_url);
          console.log("Signature URL:", this.signature_url);
        } catch (error) {
          console.error("Error saving signature:", error);
          // alert("Failed to save signature. Please try again.");
        }
      }, 'image/png', 0.9); // Quality parameter for PNG
    }
  },
};
</script>

<style scoped>
input[type="checkbox"]:checked+span {
  background-color: #f8951e;
}

input[type="checkbox"]:checked+span svg {
  display: block;
}

.cursor-crosshair {
  cursor: crosshair;
}

@media (max-width: 640px) {
  canvas {
    max-width: 100%;
    height: auto;
  }
}
</style>